import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FaEye, FaEyeSlash, FaUser, FaLock, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify'; // Si usas react-toastify
import api from '../services/api';

// Convertir iconos a componentes React válidos
const Eye = FaEye as React.ComponentType;
const EyeSlash = FaEyeSlash as React.ComponentType;
const User = FaUser as React.ComponentType;
const Lock = FaLock as React.ComponentType;
const ChevronDown = FaChevronDown as React.ComponentType;
const ChevronUp = FaChevronUp as React.ComponentType;

interface LoginFormData {
  email: string;
  password: string;
}

const LoginContainer = styled.div`
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-orange) 100%);


@media (max-width: 768px) {
  margin-top: -50px;
}
  `;

const LoginCard = styled.div`
  background: white;
  border-radius: var(--border-radius);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  padding: 3rem;
  width: 100%;
  max-width: 400px;
`;

const Logo = styled.h1`
  text-align: center;
  color: var(--primary-blue);
  margin-bottom: 2rem;
  font-size: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-medium);
  z-index: 1;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 2px solid ${props => props.hasError ? '#e74c3c' : 'var(--gray-light)'};
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
  
  &::placeholder {
    color: var(--gray-medium);
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--gray-medium);
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: var(--primary-blue);
  }
`;

const ErrorMessage = styled.span`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 0.25rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px;
  background: var(--primary-blue);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover:not(:disabled) {
    background: #081050;
  }
  
  &:disabled {
    background: var(--gray-medium);
    cursor: not-allowed;
  }
`;

const LinkContainer = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  
  a {
    color: var(--primary-blue);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      color: var(--primary-orange);
    }
  }
`;

const Divider = styled.div`
  text-align: center;
  margin: 1.5rem 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--gray-light);
  }
  
  span {
    background: white;
    padding: 0 1rem;
    color: var(--gray-medium);
    position: relative;
  }
`;

const TestAccountsContainer = styled.div`
  margin-top: 1rem;
  border: 1px solid var(--gray-light);
  border-radius: var(--border-radius);
  overflow: hidden;
`;

const TestAccountsHeader = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: #f8f9fa;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 0.9rem;
  color: var(--gray-dark);
  transition: var(--transition);
  
  &:hover {
    background: #e9ecef;
  }
  
  span {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const TestAccountsList = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop)
})<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '200px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: white;
`;

const TestAccountItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-top: 1px solid var(--gray-light);
  background: white;
  text-align: left;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:first-child {
    border-top: none;
  }
`;

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AccountEmail = styled.span`
  font-weight: 500;
  color: var(--gray-dark);
  font-size: 0.9rem;
`;

const AccountRole = styled.span<{ role: string }>`
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  background: ${props => props.role === 'admin' ? '#e3f2fd' : '#f3e5f5'};
  color: ${props => props.role === 'admin' ? '#1976d2' : '#7b1fa2'};
  width: fit-content;
`;

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showTestAccounts, setShowTestAccounts] = useState(false);
  const { login, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetStep, setResetStep] = useState<1 | 2>(1);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  
  // Cuentas de prueba
  const testAccounts = [
    {
      email: 'admin@admin.com',
      password: 'admin1234',
      role: 'admin',
      name: 'Administrador'
    },
    {
      email: 'pruebaCliente@cliente.com',
      password: 'cliente1234',
      role: 'customer',
      name: 'Cliente Prueba'
    }
  ];
  
  const from = (location.state as any)?.from?.pathname || '/';
  
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError(); // Limpiar errores previos
      await login({ email: data.email, password: data.password });
      toast.success('¡Bienvenido!');
      navigate(from, { replace: true });
    } catch (error: any) {
      // El error ya se maneja en el AuthContext
      toast.error(error.response?.data?.message || 'Error al iniciar sesión');
    }
  };
  const openResetModal = () => {
    setShowResetModal(true);
    setResetStep(1);
    setResetEmail('');
    setResetCode('');
    setResetNewPassword('');
    setResetConfirmPassword('');
    setShowResetPassword(false);
    setShowResetConfirmPassword(false);
  };

  const closeResetModal = () => {
    setShowResetModal(false);
  };

  const requestResetCode = async () => {
    try {
      if (!resetEmail) {
        toast.error('Ingresa tu correo');
        return;
      }
      await api.post('/auth/password-reset/request', { email: resetEmail });
      toast.success('Te enviamos un código a tu correo');
      setResetStep(2);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'No pudimos enviar el código');
    }
  };

  const submitResetPassword = async () => {
    try {
      if (!resetEmail || !resetCode || !resetNewPassword || !resetConfirmPassword) {
        toast.error('Completa todos los campos');
        return;
      }
      
      if (resetNewPassword !== resetConfirmPassword) {
        toast.error('Las contraseñas no coinciden');
        return;
      }
      
      if (resetNewPassword.length < 6) {
        toast.error('La contraseña debe tener al menos 6 caracteres');
        return;
      }
      
      await api.post('/auth/password-reset/reset', { email: resetEmail, code: resetCode, newPassword: resetNewPassword });
      toast.success('Tu contraseña fue actualizada');
      setShowResetModal(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'No pudimos actualizar la contraseña');
    }
  };
  
  const handleTestAccountSelect = (account: typeof testAccounts[0]) => {
    // Llenar los campos del formulario con las credenciales de la cuenta de prueba
    setValue('email', account.email);
    setValue('password', account.password);
    setShowTestAccounts(false); // Cerrar el menú desplegable
    toast.info(`Credenciales de ${account.name} cargadas`);
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>Grupo Vargas</Logo>
        
        <Form onSubmit={handleSubmit(onSubmit)}>
          <InputGroup>
            <InputIcon>
              <User />
            </InputIcon>
            <Input
              type="email"
              placeholder="Correo electrónico"
              hasError={!!errors.email}
              {...register('email', {
                required: 'El correo es requerido',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Correo electrónico inválido'
                }
              })}
            />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <Lock />
            </InputIcon>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Contraseña"
              hasError={!!errors.password}
              {...register('password', {
                required: 'La contraseña es requerida',
                minLength: {
                  value: 6,
                  message: 'La contraseña debe tener al menos 6 caracteres'
                }
              })}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeSlash /> : <Eye />}
            </PasswordToggle>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </InputGroup>

          
          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </SubmitButton>
        </Form>
        <Divider>
          <span>¿No tienes cuenta?</span>
        </Divider>

        <LinkContainer>
          <Link to="/register">Crear cuenta nueva</Link>
        </LinkContainer>
        <LinkContainer>
          <button type="button" onClick={openResetModal} style={{ background: 'none', border: 'none', color: 'var(--primary-blue)', cursor: 'pointer' }}>
            ¿Olvidaste tu contraseña?
          </button>
        </LinkContainer>
      </LoginCard>
      {showResetModal && (
        <div className="reset-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="reset-modal-card" style={{ background: 'white', borderRadius: 'var(--border-radius)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', padding: '1.5rem', width: '100%', maxWidth: 420 }}>
            <h3 style={{ margin: '0 0 1rem', color: 'var(--primary-blue)' }}>Recuperar contraseña</h3>
            {resetStep === 1 ? (
              <>
                <InputGroup>
                  <Input type="email" placeholder="Tu correo" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} />
                </InputGroup>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: '1rem' }}>
                  <SubmitButton type="button" onClick={requestResetCode}>Enviar código</SubmitButton>
                  <button type="button" onClick={closeResetModal} style={{ background: 'none', border: 'none', color: 'var(--gray-dark)', cursor: 'pointer' }}>Cancelar</button>
                </div>
              </>
            ) : (
              <>
                <InputGroup>
                  <Input type="text" placeholder="Código de verificación" value={resetCode} onChange={(e) => setResetCode(e.target.value)} />
                </InputGroup>
                <InputGroup>
                  <Input 
                    type={showResetPassword ? 'text' : 'password'} 
                    placeholder="Nueva contraseña" 
                    value={resetNewPassword} 
                    onChange={(e) => setResetNewPassword(e.target.value)} 
                    style={{ paddingRight: '40px' }}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                  >
                    {showResetPassword ? <EyeSlash /> : <Eye />}
                  </PasswordToggle>
                </InputGroup>
                <InputGroup>
                  <Input 
                    type={showResetConfirmPassword ? 'text' : 'password'} 
                    placeholder="Confirmar contraseña" 
                    value={resetConfirmPassword} 
                    onChange={(e) => setResetConfirmPassword(e.target.value)} 
                    style={{ paddingRight: '40px' }}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                  >
                    {showResetConfirmPassword ? <EyeSlash /> : <Eye />}
                  </PasswordToggle>
                </InputGroup>
                {resetNewPassword && resetConfirmPassword && resetNewPassword !== resetConfirmPassword && (
                  <div style={{ color: '#e74c3c', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    Las contraseñas no coinciden
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: '1rem' }}>
                  <SubmitButton type="button" onClick={submitResetPassword}>Cambiar contraseña</SubmitButton>
                  <button type="button" onClick={closeResetModal} style={{ background: 'none', border: 'none', color: 'var(--gray-dark)', cursor: 'pointer' }}>Cancelar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </LoginContainer>
  );
};

export default Login;